import { ElevateApps } from '@/graphql/types';
import { ElevateAppRoutes } from '@/utilities/page-definitions';
import { encodeToPublicID } from '@/utilities/uuid-encoder';

export enum Actions {
  CREATE,
  DETAIL,
  LIST,
  UPDATE,
}

export enum Models {
  INTERVIEWS,
  INTERVIEW_TEMPLATES,
  TEAMS,
}

type IModelKeys = {
  [K in Models]: string;
};

export const Prefixes: IModelKeys = {
  [Models.TEAMS]: 'teams',
  [Models.INTERVIEWS]: 'interviews',
  [Models.INTERVIEW_TEMPLATES]: 'interview-templates',
} as const;

export const DynamicSegmentParams: IModelKeys = {
  [Models.INTERVIEWS]: 'interviewID',
  [Models.INTERVIEW_TEMPLATES]: 'interviewTemplateID',
  [Models.TEAMS]: 'teamID',
} as const;

export const DynamicSegments: IModelKeys = {
  [Models.INTERVIEWS]: `:${DynamicSegmentParams[Models.INTERVIEWS]}`,
  [Models.INTERVIEW_TEMPLATES]: `:${DynamicSegmentParams[Models.INTERVIEW_TEMPLATES]}`,
  [Models.TEAMS]: `:${DynamicSegmentParams[Models.TEAMS]}`,
} as const;

type IStaticSegments = {
  [Actions.CREATE]: string;
  [Actions.UPDATE]: string;
};

export const StaticSegments: IStaticSegments = {
  [Actions.CREATE]: 'create',
  [Actions.UPDATE]: 'update',
};

type IModelPath = {
  [K in Actions]: string | ((id: string | undefined) => string);
};

const Paths: IModelPath = {
  [Actions.CREATE]: StaticSegments[Actions.CREATE],
  [Actions.DETAIL]: (id: string | undefined) => {
    if (!id) throw new Error('required ID not provided');
    return encodeToPublicID(id) || id;
  },
  [Actions.LIST]: '',
  [Actions.UPDATE]: (id: string | undefined) => {
    if (!id) throw new Error('required ID not provided');
    return `${encodeToPublicID(id) || id}/${StaticSegments[Actions.UPDATE]}`;
  },
} as const;

type Options = {
  hash?: string;
  root?: string;
  searchParams?: URLSearchParams;
};

const URL_PREFIX = 'https://aws.dev';

export function pathTo(action: Actions, model: Models, id?: string, options?: Options): string {
  const actionPath: string = typeof Paths[action] === 'function' ? Paths[action].call(null, id) : Paths[action];
  const path: string = actionPath === '' ? `/${Prefixes[model]}` : `/${Prefixes[model]}/${actionPath}`;
  const root = options?.root ?? ElevateAppRoutes[ElevateApps.QUESTION_BANK];
  const url = new URL(`${root}${path}`, URL_PREFIX);

  if (options?.hash) {
    url.hash = options.hash;
  }

  if (options?.searchParams) {
    for (const [key, value] of options.searchParams) {
      url.searchParams.set(key, value);
    }
  }

  const final = url.toString();

  /*
   * Slice the absolute part of the URL off to get the relative bits, including
   * the hash and search params.
   */
  return final.slice(URL_PREFIX.length);
}

if (import.meta.vitest) {
  const { describe, test } = import.meta.vitest;
  const id = '05b07364-b578-4113-8f34-a1fb0d24d44c'; // Just a random, valid UUID
  const encodedID = encodeToPublicID(id);

  function prefixedToString(path: string): string {
    const url = new URL(`${ElevateAppRoutes[ElevateApps.QUESTION_BANK]}/${path}`, URL_PREFIX);

    return url.toString().slice(URL_PREFIX.length);
  }

  describe('pathTo', () => {
    for (const key of Object.keys(Models)) {
      // For some unholy reason Object.keys on an enum returns both the values
      // and the keys in a single array. This check skips the numeric keys.
      if (!Number.isNaN(Number(key))) continue;

      const value = Models[key];

      test(`${value} index path`, ({ expect }) => {
        expect(pathTo(Actions.LIST, value)).toEqual(prefixedToString(Prefixes[value]));
      });
      test(`${value} create path`, ({ expect }) => {
        expect(pathTo(Actions.CREATE, value)).toEqual(prefixedToString(`${Prefixes[value]}/create`));
      });
      test(`${value} detail path`, ({ expect }) => {
        expect(pathTo(Actions.DETAIL, value, id)).toEqual(prefixedToString(`${Prefixes[value]}/${encodedID}`));
        // Non UUID values should pass through.
        expect(pathTo(Actions.DETAIL, value, '22')).toEqual(prefixedToString(`${Prefixes[value]}/22`));
      });
      test(`${value} detail path without ID`, ({ expect }) => {
        expect(() => pathTo(Actions.DETAIL, value)).toThrowError(/required ID not provided/);
      });
      test(`${value} update path`, ({ expect }) => {
        expect(pathTo(Actions.UPDATE, value, id)).toEqual(prefixedToString(`${Prefixes[value]}/${encodedID}/update`));
        // Non UUID values should pass through.
        expect(pathTo(Actions.UPDATE, value, '22')).toEqual(prefixedToString(`${Prefixes[value]}/22/update`));
      });
      test(`${value} update path without ID`, ({ expect }) => {
        expect(() => pathTo(Actions.UPDATE, value)).toThrowError(/required ID not provided/);
      });
    }

    // URL hash usage in the function doesn't vary based on action, so I just
    // added a single test to confirm that it works at all.
    test('URL hash', ({ expect }) => {
      expect(pathTo(Actions.LIST, Models.TEAMS, undefined, { hash: 'urlHash' })).toEqual(
        prefixedToString(`${Prefixes[Models.TEAMS]}#urlHash`)
      );
    });

    // Search param usage in the function doesn't vary based on action, so I
    // just added a single test to confirm that it works at all.
    test('search params', ({ expect }) => {
      expect(
        pathTo(Actions.LIST, Models.TEAMS, undefined, { searchParams: new URLSearchParams({ query: 'string' }) })
      ).toEqual(prefixedToString(`${Prefixes[Models.TEAMS]}?query=string`));
    });

    // Root usage in the function doesn't vary based on action, so I just added
    // a single test to confirm that it works at all.
    test('alternative root path', ({ expect }) => {
      const root = '/fizbuzz';
      expect(pathTo(Actions.LIST, Models.TEAMS, undefined, { root })).toEqual(`${root}/${Prefixes[Models.TEAMS]}`);
    });
  });

  describe('dynamic segment', () => {
    for (const key of Object.keys(Models)) {
      // For some unholy reason Object.keys on an enum returns both the values
      // and the keys in a single array. This check skips the numeric keys.
      if (!Number.isNaN(Number(key))) continue;

      const value = Models[key];

      test('dynamic segments should be based on dynamic segment params', ({ expect }) => {
        expect(DynamicSegments[value]).toEqual(`:${DynamicSegmentParams[value]}`);
      });
    }
  });
}
